import { TrashAltIcon } from "assets/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { IconButton } from "ui";

import {
  TEMPLATE_STATUS_COLORS,
  TEMPLATE_STATUS_LABELS
} from "../../constants";
import { MarketingTemplate } from "../../marketing.types";
import { StatusBadge } from "../StatusBadge";

import styles from "./TemplateCards.module.scss";

interface TemplateCardsProps {
  templates: MarketingTemplate[];
  isLoading?: boolean;
  onEdit: (template: MarketingTemplate) => void;
  onDelete: (template: MarketingTemplate) => void;
}

const TemplateCards = ({
  templates,
  isLoading,
  onEdit,
  onDelete
}: TemplateCardsProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <Skeleton count={4} height={110} borderRadius={16} />;
  }

  if (!templates.length) {
    return <p className={styles.empty}>{t("No templates registered yet")}</p>;
  }

  return (
    <div className={styles.list}>
      {templates.map((template) => (
        <div
          key={template.id}
          className={styles.card}
          role="button"
          tabIndex={0}
          onClick={() => onEdit(template)}
          onKeyDown={(event) => {
            if (event.key === "Enter") onEdit(template);
          }}
        >
          <div className={styles.card__main}>
            <div className={styles.card__header}>
              <span className={styles.card__name}>{template.name}</span>
              <StatusBadge
                label={TEMPLATE_STATUS_LABELS[template.status]}
                color={TEMPLATE_STATUS_COLORS[template.status]}
              />
            </div>

            <p className={styles.card__body}>{template.bodyText}</p>

            <div className={styles.card__meta}>
              <span className={styles.card__metaChip}>
                {template.language.toUpperCase()}
              </span>
              <span className={styles.card__metaChip}>
                {t(template.category)}
              </span>
              <span className={styles.card__metaChip}>
                {template.variableCount} {t("variables")}
              </span>
              <span className={styles.card__date}>
                {t("Updated")}{" "}
                {moment(template.updatedAt).format("DD/MM/YYYY")}
              </span>
            </div>
          </div>

          <IconButton
            icon={<TrashAltIcon fill="#ff0033" width={20} height={20} />}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(template);
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default TemplateCards;
